/**
 * Terms of Service Page – super clean, minimal
 */
import { useEffect } from 'react';
import { useCustomTranslation } from '../hooks';
import { Box, Container, Typography, Stack, Divider } from '@mui/material';
import { trackPageView } from '../utils/analytics';

export default function Terms() {
  const { i18n } = useCustomTranslation();
  const isEn = i18n.language === 'en';

  useEffect(() => {
    trackPageView('/terms', 'Terms of Service');
    document.title = isEn ? 'Terms of Service | PayWise365' : '服務條款 | PayWise365';
  }, [isEn]);

  /* ── Shared section component ── */
  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <>
      <Box>
        <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 600 }}>
          {title}
        </Typography>
        {children}
      </Box>
      <Divider />
    </>
  );

  const P = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mb: 1 }}>
      {children}
    </Typography>
  );

  const Li = ({ children }: { children: React.ReactNode }) => (
    <li>
      <Typography variant="body2" color="text.secondary" component="span">
        {children}
      </Typography>
    </li>
  );

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ mb: 1, fontWeight: 700 }}>
            {isEn ? 'Terms of Service' : '服務條款'}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
            {isEn ? 'Please read these terms carefully' : '請仔細閱讀以下條款'}
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          <strong>{isEn ? 'Last Updated:' : '最後更新：'}</strong> February 7, 2026
        </Typography>

        <Stack spacing={3}>
          {/* 1 */}
          <Section title={isEn ? '1. Acceptance of Terms' : '1. 接受條款'}>
            <P>
              {isEn
                ? 'By accessing or using PayWise365 (www.paywise365.com), you agree to be bound by these Terms of Service. If you do not agree, please do not use our website.'
                : '使用 PayWise365（www.paywise365.com）即表示你同意受本服務條款約束。如不同意，請勿使用本網站。'}
            </P>
          </Section>

          {/* 2 */}
          <Section title={isEn ? '2. Description of Service' : '2. 服務描述'}>
            <P>
              {isEn
                ? 'PayWise365 provides free online financial calculators including mortgage, loan repayment, savings, investment, and other planning tools. We also publish educational blog content related to personal finance in Hong Kong.'
                : 'PayWise365 提供免費線上理財計算器，涵蓋按揭、貸款還款、儲蓄、投資及其他規劃工具。我們亦發布與香港個人理財相關嘅教育性網誌內容。'}
            </P>
          </Section>

          {/* 3 */}
          <Section title={isEn ? '3. No Financial Advice' : '3. 非財務建議'}>
            <P>
              {isEn
                ? 'The tools and content on PayWise365 are for informational and educational purposes only. They do not constitute financial, legal, tax, or investment advice. Always consult a licensed professional before making financial decisions.'
                : '本網站提供嘅工具及內容僅供參考及教育用途，不構成財務、法律、稅務或投資建議。作出任何財務決定前，請諮詢持牌專業人士。'}
            </P>
          </Section>

          {/* 4 */}
          <Section title={isEn ? '4. Accuracy of Calculations' : '4. 計算準確性'}>
            <P>
              {isEn
                ? 'While we strive to ensure our calculators are accurate, results are estimates only. Actual loan payments, interest amounts, and financial outcomes may differ due to rounding, fees, policy changes, or other factors. We are not liable for any discrepancies.'
                : '雖然我們盡力確保計算器準確，但結果僅供估算。實際還款額、利息及財務結果可能因進位、費用、政策變動或其他因素而有差異。我們不對任何差異承擔責任。'}
            </P>
          </Section>

          {/* 5 */}
          <Section title={isEn ? '5. User Conduct' : '5. 用戶行為'}>
            <P>{isEn ? 'You agree not to:' : '你同意不會：'}</P>
            <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0 }}>
              <Li>{isEn ? 'Use the service for any unlawful purpose' : '將本服務用於任何非法用途'}</Li>
              <Li>{isEn ? 'Attempt to disrupt or compromise the website' : '試圖干擾或破壞本網站'}</Li>
              <Li>{isEn ? 'Scrape, copy, or redistribute content without permission' : '未經許可抓取、複製或轉載內容'}</Li>
              <Li>{isEn ? 'Misrepresent your identity or affiliation' : '冒充身份或隸屬關係'}</Li>
            </Box>
          </Section>

          {/* 6 */}
          <Section title={isEn ? '6. Intellectual Property' : '6. 知識產權'}>
            <P>
              {isEn
                ? 'All content on PayWise365 — including text, design, graphics, code, and logos — is owned by PayWise365 and protected by applicable copyright and intellectual property laws. You may not reproduce or distribute any part of this website without prior written consent.'
                : 'PayWise365 上所有內容——包括文字、設計、圖像、代碼及標誌——均為 PayWise365 所有，並受適用版權及知識產權法律保護。未經事先書面同意，不得複製或分發本網站任何部分。'}
            </P>
          </Section>

          {/* 7 */}
          <Section title={isEn ? '7. Affiliate Links & Disclosure' : '7. 聯盟連結及披露'}>
            <P>
              {isEn
                ? 'Some pages on PayWise365 contain affiliate links. If you click on an affiliate link and sign up for a product or make a purchase, we may receive a small commission at no additional cost to you. As an Amazon Associate, PayWise365 earns from qualifying purchases. Affiliate relationships do not influence the data, rankings, or editorial content on our site.'
                : '本網站部分頁面包含聯盟連結。如你透過聯盟連結註冊產品或購物，我們可能會收取少量佣金，而不會增加你的費用。作為 Amazon Associate，PayWise365 從合資格購買中賺取佣金。聯盟關係不會影響本網站嘅數據、排名或編輯內容。'}
            </P>
          </Section>

          {/* 8 */}
          <Section title={isEn ? '8. Third-Party Links & Services' : '8. 第三方連結及服務'}>
            <P>
              {isEn
                ? 'Our website may contain links to third-party websites or use third-party services (e.g. Google Analytics, Google AdSense). We are not responsible for the content, privacy practices, or availability of these external sites.'
                : '本網站可能包含第三方網站連結或使用第三方服務（如 Google Analytics、Google AdSense）。我們不對這些外部網站嘅內容、私隱政策或可用性負責。'}
            </P>
          </Section>

          {/* 9 */}
          <Section title={isEn ? '9. Limitation of Liability' : '9. 責任限制'}>
            <P>
              {isEn
                ? 'PayWise365 is provided "as is" without warranties of any kind. To the fullest extent permitted by law, we shall not be liable for any direct, indirect, incidental, or consequential damages arising from your use of the website.'
                : 'PayWise365 按「現狀」提供，不作任何形式嘅保證。在法律允許嘅最大範圍內，我們不對因使用本網站而產生嘅任何直接、間接、附帶或後果性損害承擔責任。'}
            </P>
          </Section>

          {/* 10 */}
          <Section title={isEn ? '10. Changes to Terms' : '10. 條款修改'}>
            <P>
              {isEn
                ? 'We may update these Terms from time to time. Changes will be posted on this page with a revised "Last Updated" date. Continued use of the site after changes constitutes acceptance of the new terms.'
                : '我們可能不時更新本條款。更改將於本頁面發布，並更新「最後更新」日期。條款更改後繼續使用本網站即表示接受新條款。'}
            </P>
          </Section>

          {/* 11 */}
          <Section title={isEn ? '11. Governing Law' : '11. 適用法律'}>
            <P>
              {isEn
                ? 'These Terms are governed by and construed in accordance with the laws of the Hong Kong Special Administrative Region. Any disputes shall be subject to the exclusive jurisdiction of the courts of Hong Kong.'
                : '本條款受香港特別行政區法律管轄並據其解釋。任何爭議均受香港法院嘅專屬管轄權管轄。'}
            </P>
          </Section>

          {/* 12 */}
          <Box>
            <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 600 }}>
              {isEn ? '12. Contact' : '12. 聯絡我們'}
            </Typography>
            <P>
              {isEn
                ? 'If you have any questions about these Terms, please contact us at:'
                : '如對本條款有任何疑問，請聯絡我們：'}
            </P>
            <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0 }}>
              <Li><strong>{isEn ? 'Email:' : '電郵：'}</strong> paywise365@gmail.com</Li>
              <Li><strong>{isEn ? 'Website:' : '網站：'}</strong> www.paywise365.com</Li>
            </Box>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
